import React from 'react';
import cn from 'classnames';
import 'react-base-table/styles.css';

import BaseTable, { AutoResizer, Column, callOrReturn } from 'react-base-table';
import { SelectionCell, SelectionHeader } from './SelectionCell';
import { connect } from '~/redux/connect';
import Icon from '~/ui/ReactML/Icon';
import { PushLink } from '~/ui/ReactML/PushLink';
import { Box, Button, Link, List, Text, WithBaseStyles, HStack } from '~/native-ui';
import { highlightFactory, renderHTML, useColor, useColorModeValue } from '~/native-ui';
import CollapseText from '~/ui/ReactML/CollapseText';
import PopupList from '~/ui/ReactML/PopupList';
import css from './css.css';
import noop from 'lodash/noop';
import set from 'lodash/set';
import get from 'lodash/get';
import filter from 'lodash/filter';
import pickBy from 'lodash/pickBy';
import has from 'lodash/has';
import { deriveHiddenColumns, getClickHandler } from './helpers';
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import isEqual from 'lodash/isEqual';
import { TimeCounter } from '../../components/TimeCounter/TimeCounter';

const DraggableContainer = sortableContainer(({ children }) => children);
const DraggableElement = sortableElement(({ children }) => children);
const DragHandle = sortableHandle(({ children }) => children);

const StyledLink = React.memo(
    React.forwardRef((props, ref) => <PushLink ref={ref} {...props} fontSize="xs" isUnderlined alignItems="center" />)
);

/**
 * 
 * React Native Web wraps an onPress event.
 * 
 * If onPress is passed, it stopsPropagation of the event, thus preventing bubbling.
 * If onClick is passed, onPress is never reached. THUS, we need to pass an onClick event to bypass the RNW onPress handling.
 * 
 * This is a problem only where we have tooltips as Native Base tooltips adds onPress prop internally to all child components.
 * 
 */

const onClick= () => {}

const defaultComponents = {
    a: StyledLink,
    Text: (props) => <CollapseText {...props} onClick={onClick} fontSize="sm" color="inherit" />,
    li: WithBaseStyles(List.Item, { baseStyle: { alignItems: 'flex-start' } }),
    ul: WithBaseStyles(PopupList, { baseStyle: { fontSize: 'xs', color: 'muted.700', alignItems: 'flex-start' } }),
    icon: React.memo((props) => <Icon {...props} lineHeight="1" />),
};

const Cell = React.memo(({ column, ...props }) => {
    return <span className={css.cellInner} data-highlight={column.highlight} {...props} />;
});

const htmlRenderer = (row, column, components = defaultComponents) => {
    return get(row, `${column.name}.rendered`, '') ? (
        <Cell column={column}>
            {renderHTML('' + get(row, `${column.name}.rendered`, ''), {
                ...defaultComponents,
                ...components,
            })}
        </Cell>
    ) : (
        ''
    );
};

const TableCell = (props) => <MemoisedTableCell {...props} />;

const HighLightText = React.memo(
    highlightFactory({
        Text: defaultComponents.Text,
    })
);

const MemoisedTableCell = React.memo(({ className, rowData, column, container, ...rest }) => {
    const { updateRowState, rowState, rowKey, searchFilterValueRef } = container.props;
    const key = `${column.key}._${rowData[rowKey]}`;
    const onExpand = React.useCallback(
        (value) => {
            updateRowState(key, value);
            container._updateRowHeights();
        },
        [key, updateRowState, container]
    );

    return htmlRenderer(rowData, column, {
        ul: React.useCallback(
            (props) => {
                const state = get(rowState.current, key, false);
                return (
                    <PopupList
                        alignItems="flex-start"
                        fontSize="inherit"
                        onExpand={onExpand}
                        isExpanded={state}
                        {...props}
                    />
                );
            },
            [onExpand]
        ),
        Text: React.useCallback(
            (props, ...args) => {
                const searchString = searchFilterValueRef?.current || '';
                const hasSearch = searchString && props.value.toLowerCase().includes(searchString.toLowerCase());
                if (!hasSearch) {
                    return <defaultComponents.Text fontSize="inherit" {...props} />;
                }

                return <HighLightText fontSize="inherit" {...props} value={props.value} searchTerm={searchString} />;
            },
            [onExpand]
        ),
    });
});

const TableHeaderCell = (props) => <MemoizedTableHeaderCell {...props} />;
const MemoizedTableHeaderCell = React.memo(({ className, column }) => {
    const justifyContent = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
    };

    return (
        <div className={css.thInner}>
            {column.disableDrag ? null : (
                <DragHandle>
                    <Icon className={css.dragHandle} value="drag-indicator" />
                </DragHandle>
            )}
            <div className={cn(className)} title={column.title}>
                <span className={css.cellInner}>
                    <Box w="100%" flexWrap="wrap" justifyContent={justifyContent[column.align]}>
                        <Text color="muted.700" isTruncated maxW="100%" fontSize="xs">
                            {column.title}
                        </Text>
                    </Box>
                </span>
            </div>
        </div>
    );
});

const DraggableHeaderCell = React.memo(({ index, disableDrag, children, ...rest }) => {
    return disableDrag ? (
        <div key={index} {...rest}>
            {' '}
            {children}{' '}
        </div>
    ) : (
        <DraggableElement key={index} index={index}>
            <div {...rest} className={cn(rest.className, css.draggableHeader)}>
                {' '}
                <div className={css.draggableHeaderInner}>{children}</div>{' '}
            </div>
        </DraggableElement>
    );
});

const SortIndicator = (props) => <MemoizedSortIndicator {...props} />;
const MemoizedSortIndicator = React.memo(({ sortOrder, className, ...props }) => {
    const type = {
        asc: 'arrow-upward',
        desc: 'arrow-downward',
    };

    return (
        <div
            className={cn(className, css.sortIcon, {
                [css.asc]: sortOrder === 'asc',
                [css.desc]: sortOrder === 'desc',
            })}
        >
            <Icon size="xs" color="inherit" px="1px" bg={'transparent'} type={type[sortOrder]} />
        </div>
    );
});

const SelectThroughOut = React.memo(
    React.forwardRef((props, ref) => {
        const { allSelected, hasSelectThroughout, perPage, total } = props;
        const noSelectThroughout = total <= perPage;

        if (noSelectThroughout) return null;
        if (!allSelected && !hasSelectThroughout) return null;

        const message = hasSelectThroughout
            ? props.trans('table.clear_select_throughout_pages')
            : props.trans('table.select_throughout_pages', {
                  count: Math.min(props.perPage, props.data.length),
                  total,
              });
        const buttonLabel = hasSelectThroughout
            ? props.trans('table.clear_select_throughout_pages_button_label')
            : props.trans('table.select_throughout_pages_button_label');

        return (
            <HStack space="2" alignItems="center" ref={ref}>
                {renderHTML(message)}{' '}
                <Button primary onClick={props.onSelectThroughtClicked} label={renderHTML(buttonLabel)}></Button>
            </HStack>
        );
    })
);

const deriveStateFromSelectedRowKeys = (rows, selectedRowKeys) => {
    const allSelectedRows = Object.values(selectedRowKeys);
    const allSelected = allSelectedRows.length && rows.every((x) => selectedRowKeys[x._id]);

    return {
        allSelected,
        selectedRowKeys,
        indeterminate: !allSelected && allSelectedRows.some(Boolean),
    };
};

const deriveColumnsWithUpdatedWidths = (el, columns) => {
    const widths = Array.from(el.querySelectorAll('table'))
        .reduce((allHeadCells, table) => allHeadCells.concat(...table.rows[0].cells), [])
        .reduce((columnWidths, cell) => {
            const columnName = cell.dataset.column;
            columnWidths[columnName] = cell.clientWidth;
            return columnWidths;
        }, {});

    return columns.map((column) => ({ ...column, width: widths[column.name] }));
};

export const tableFactory = () => {
    return class Table extends React.PureComponent {
        static defaultProps = {
            columns: [],
            data: [],
            columnOrder: {},
            onSelectedRowsChange: noop,
            onSelectAll: noop,
            onRowSelect: noop,
            onColumnResize: noop,
            onColumnResizeEnd: noop,
            onRowClick: noop,
            onRowDoubleClick: noop,
            onSortChange: noop,
            onColumnReorder: noop,
        };

        lastClickCellIdRef = React.createRef();
        tableRef = React.createRef();
        rowStateRef = React.createRef({});

        constructor(props) {
            super(props);
            const { selectedRowKeys, defaultSelectedRowKeys, expandedRowKeys, defaultExpandedRowKeys } = props;

            this.state = {
                selectThroughoutRef: null,
                hasSelectThroughout: false,
                ...deriveStateFromSelectedRowKeys(
                    props.data,
                    (selectedRowKeys !== undefined ? selectedRowKeys : defaultSelectedRowKeys) || {}
                ),
                expandedRowKeys: (expandedRowKeys !== undefined ? expandedRowKeys : defaultExpandedRowKeys) || {},
            };
        }

        static getDerivedStateFromProps(props, state) {
            const existingState = deriveStateFromSelectedRowKeys(props.data, state.selectedRowKeys);
            if (state.hasSelectThroughout) {
                const rowKey = props.rowKey;
                const selectedRowKeys = { ...state.selectedRowKeys };
                const newRows = filter(props.data, (row) => !has(selectedRowKeys, row[rowKey]));
                if (!newRows.length) return existingState;

                for (const row of newRows) {
                    selectedRowKeys[row[rowKey]] = true;
                }

                return deriveStateFromSelectedRowKeys(props.data, selectedRowKeys);
            }

            return existingState;
        }

        getSnapshotBeforeUpdate(prevProps, prevState) {
            return !isEqual(prevProps.data, this.props.data) ? null : this.lastClickCellIdRef.current;
        }

        componentDidUpdate(prevProps, prevState, snapshot) {
            this.lastClickCellIdRef.current = snapshot;
            if (prevProps.hasSelections && prevProps.hasSelections !== this.props.hasSelections) {
                this.setState({
                    selectedRowKeys: {},
                    hasSelectThroughout: false,
                });
            }
        }

        setSelectThroughtRef = (selectThroughoutRef) => this.setState({ selectThroughoutRef });

        /**
         * Set `selectedRowKeys` manually.
         * This method is available only if `selectedRowKeys` is uncontrolled.
         *
         * @param {array} selectedRowKeys
         */
        setSelectedRowKeys(selectedRowKeys) {
            // if `selectedRowKeys` is controlled
            if (this.props.selectedRowKeys !== undefined) return;

            this.setState(deriveStateFromSelectedRowKeys(this.props.data, selectedRowKeys));
        }

        /**
         * See BaseTable#setExpandedRowKeys
         */
        setExpandedRowKeys(expandedRowKeys) {
            // if `expandedRowKeys` is controlled
            if (this.props.expandedRowKeys !== undefined) return;

            this.setState({
                expandedRowKeys: Array.from(expandedRowKeys),
            });
        }

        /* some other custom methods and proxy methods */
        getMultiSelectRows({ checked, rowData, event }) {
            const shiftKey = event.nativeEvent.shiftKey;
            const order = rowData.order;
            const key = rowData[this.props.rowKey];
            const lastClickedRow = this.lastClickCellIdRef.current;
            const selectedRowKeys = { ...this.state.selectedRowKeys };

            this.lastClickCellIdRef.current = { key, checked, order };
            if (!shiftKey || !lastClickedRow || lastClickedRow.key === key) {
                selectedRowKeys[key] = checked;
                return selectedRowKeys;
            }

            const [startIndex, endIndex] = [order, lastClickedRow.order].sort();
            this.props.data.slice(startIndex, endIndex + 1).forEach((row) => {
                const key = row[this.props.rowKey];
                selectedRowKeys[key] = checked;
            });

            return selectedRowKeys;
        }

        _handleSelectChange = ({ checked, rowData, rowIndex, event }) => {
            const shiftKey = event.nativeEvent.shiftKey;
            let selectedRowKeys = this.getMultiSelectRows({ checked, rowData, rowIndex, event });

            // if `selectedRowKeys` is uncontrolled, update internal state
            if (this.props.selectedRowKeys === undefined) {
                const derivedState = deriveStateFromSelectedRowKeys(this.props.data, selectedRowKeys);
                selectedRowKeys = derivedState.selectedRowKeys;
                this.setState(derivedState);
            }

            this.props.onRowSelect({ checked, rowData, rowIndex, shiftKey });
            this.props.onSelectedRowsChange({
                selectThroughout: this.state.hasSelectThroughout,
                rowIds: pickBy(selectedRowKeys, (value) => value !== this.state.hasSelectThroughout),
            });
        };

        _handleSelectAllRows = ({ checked }) => {
            let selectedRowKeys = { ...this.state.selectedRowKeys };

            for (const rowData of this.props.data) {
                const key = rowData[this.props.rowKey];
                selectedRowKeys[key] = checked;
            }

            // if `selectedRowKeys` is uncontrolled, update internal state
            if (this.props.selectedRowKeys === undefined) {
                const derivedState = deriveStateFromSelectedRowKeys(this.props.data, selectedRowKeys);
                selectedRowKeys = derivedState.selectedRowKeys;
                this.setState(derivedState);
            }

            this.props.onSelectedRowsChange({
                selectThroughout: this.state.hasSelectThroughout,
                rowIds: pickBy(selectedRowKeys, (value) => value !== this.state.hasSelectThroughout),
            });
        };

        _handleSelectThroughout = () => {
            const checked = !this.state.hasSelectThroughout;

            /**
             *
             * If not checked, reset selection state.
             *
             */
            if (!checked) {
                return this.setState({
                    hasSelectThroughout: checked,
                    ...deriveStateFromSelectedRowKeys(this.props.data, {}),
                });
            }

            /**
             *
             * Set state of all rows to true.
             *
             */
            const selectedRowKeys = { ...this.state.selectedRowKeys };
            for (const key of Object.keys(selectedRowKeys)) {
                selectedRowKeys[key] = checked;
            }

            this.setState(
                {
                    hasSelectThroughout: checked,
                    selectedRowKeys,
                },
                () => {
                    this._handleSelectAllRows({ checked });
                }
            );
        };

        _handleRowClick = ({ rowData }) => {
            this.props.onRowClick(rowData);
        };

        _handleRowDoubleClick = ({ rowData }) => {
            this.props.onRowDoubleClick(rowData);
        };

        _handleRowClickEvents = getClickHandler(
            (e) => this._handleRowClick(e),
            (e) => this._handleRowDoubleClick(e),
            200
        );

        _handleColumnResize = ({ column: { key, width } }) => {
            this.props.onColumnResize({ ...this.props.columnWidths, [key]: width });
        };

        _handleColumnResizeEnd = ({ column: { key, width } }) => {
            this.props.onColumnResizeEnd({ ...this.props.columnWidths, [key]: width });
        };

        _handleUpdateRowState = (key, value) => {
            this.rowStateRef = set({ ...this.rowStateRef.current }, key, value);
        };

        _handleColumnReorder = ({ oldIndex, newIndex }) => {
            const checkboxColumnOffset = +this.props.selectable;

            oldIndex -= checkboxColumnOffset;
            newIndex -= checkboxColumnOffset;

            this.props.onColumnReorder({
                ...this.props.columnOrder,
                ...this.props.deriveNewListOrder(this.props.columns, oldIndex, newIndex),
            });
        };

        _handleColumnSort = (sortOptions) => {
            this.props.onSortChange({
                field: get(sortOptions, 'key'),
                direction: get(sortOptions, 'order'),
            });
        };

        _rowClassName = ({ rowData, rowIndex }) => {
            const { rowClassName, rowKey } = this.props;
            const { selectedRowKeys } = this.state;

            const rowClass = rowClassName ? callOrReturn(rowClassName, { rowData, rowIndex }) : '';
            const key = rowData[rowKey];

            return cn(rowClass, css.tr, css.dataRow, 'row-background', {
                [css.row_selected]: !this.state.hasSelectThroughout && selectedRowKeys[key],
            });
        };

        _deriveHeightProps(height) {
            return this.props.autoHeight
                ? {
                      height: this.state.selectThroughoutRef
                          ? Math.max(height - this.state.selectThroughoutRef.clientHeight, 100)
                          : height,
                  }
                : { maxHeight: Infinity };
        }

        getColumnDragContainer = () => {
            return this.tableRef.current.getDOMNode();
        };

        getHelperContainer = () => {
            return this.tableRef.current.getDOMNode().querySelector('.BaseTable__table-main');
        };

        setTableRef = (node) => {
            this.tableRef.current = node;
            this.props.forwardedRef && this.props.forwardedRef(node);
        };

        rowEventHandlers = {
            onClick: this._handleRowClickEvents,
        };

        components = { TableCell, TableHeaderCell, SortIndicator, ExpandIcon: () => null };

        render() {
            const { columns, children, selectable, selectionColumnProps, autoHeight, forwardedRef, ...rest } =
                this.props;

            const autoSizerHeight = autoHeight ? null : Infinity;
            const { selectedRowKeys, indeterminate, allSelected } = this.state;

            // you'd better memoize this operation
            let _columns = columns || [];

            if (columns.length === 0) return null;
            if (selectable) {
                const selectionColumn = {
                    width: 30,
                    flexShrink: 0,
                    resizable: false,
                    frozen: Column.FrozenDirection.LEFT,
                    headerRenderer: SelectionHeader,
                    cellRenderer: SelectionCell,
                    ...selectionColumnProps,
                    [this.props.rowKey]: '__selection__',
                    rowKey: this.props.rowKey,
                    selectedRowKeys,
                    indeterminate,
                    allSelected,
                    onChange: this._handleSelectChange,
                    onSelectAll: this._handleSelectAllRows,
                    headerClassName: css.checkboxCell,
                    className: css.checkboxCell,
                    i18n: {
                        selectAll: this.props.trans('table.selectAll'),
                        unselectAll: this.props.trans('table.unselectAll'),
                        selectRow: this.props.trans('table.selectRow'),
                        unselectRow: this.props.trans('table.unselectRow'),
                    },
                };
                _columns = [selectionColumn, ..._columns];
            }

            return (
                <>
                    <SelectThroughOut
                        {...rest}
                        ref={this.setSelectThroughtRef}
                        allSelected={allSelected}
                        hasSelectThroughout={this.state.hasSelectThroughout}
                        onSelectThroughtClicked={this._handleSelectThroughout}
                    />

                    <AutoResizer height={autoSizerHeight} onResize={this.props.onResize}>
                        {({ height, width }) => {
                            return (
                                <DraggableContainer
                                    disabled={this.props.canReorderColumns}
                                    useDragHandle
                                    getContainer={this.getColumnDragContainer}
                                    helperContainer={this.getHelperContainer}
                                    onSortEnd={this._handleColumnReorder}
                                    axis="x"
                                    lockAxis="X"
                                >
                                    <BaseTable
                                        {...rest}
                                        {...this._deriveHeightProps(height)}
                                        fixed
                                        headerHeight={30}
                                        ref={this.setTableRef}
                                        width={width}
                                        className={cn(css.table, rest.className)}
                                        estimatedRowHeight={100}
                                        columns={_columns}
                                        headerClassName={cn(css.tr, css.headRow, 'row-background')}
                                        updateRowState={this._handleUpdateRowState}
                                        rowState={this.rowStateRef}
                                        rowClassName={this._rowClassName}
                                        onColumnResize={this._handleColumnResize}
                                        onColumnResizeEnd={this._handleColumnResizeEnd}
                                        onColumnSort={this._handleColumnSort}
                                        components={this.components}
                                        headerCellProps={this.props.headerCellProps}
                                        cellProps={this.props.rowCellProps}
                                        rowEventHandlers={this.rowEventHandlers}
                                    />
                                </DraggableContainer>
                            );
                        }}
                    </AutoResizer>
                </>
            );
        }
    };
};

export const autosizedTableFactory = (Table) => {
    return class TableWithAutosizedColumns extends React.PureComponent {
        static defaultProps = {
            ...Table.defaultProps,
            columnWidths: {},
        };

        scrollbarWidth = 10;
        tableWidth = 0;

        shadowRef = React.createRef();
        tableRef = React.createRef();

        state = {
            columns: [],
            columnWidths: {},
        };

        componentDidUpdate(prevProps) {
            if (prevProps.columns !== this.props.columns) {
                this.updateColumnWidths();
            }
        }

        setColumnWidths = (node) => {
            this.shadowRef = node;
            this.updateColumnWidths();
        };

        updateColumnWidths() {
            if (!this.shadowRef) return;

            this.setState({
                columns: deriveColumnsWithUpdatedWidths(this.shadowRef, this.props.columns),
            });
        }

        getColumns({ fixed = false } = {}) {
            const columns = this.props.columns.filter((column) =>
                fixed ? this.props.columnWidths[column.key] : !this.props.columnWidths[column.key]
            );

            if (fixed && this.props.selectable) {
                columns.unshift({ width: 30, key: '__selection__', className: css.checkboxCell });
            }

            return columns;
        }

        getHeaders(options = {}) {
            const headers = this.getColumns(options).map((column, i) => (
                <th
                    key={i}
                    style={{ minWidth: column.width, maxWidth: column.width }}
                    data-column={column.name}
                    className={css.th}
                    width={column.width}
                >
                    <TableHeaderCell column={column} />
                </th>
            ));

            return (
                <thead>
                    <tr key={'header'} className={css.tr}>
                        {headers}
                    </tr>
                </thead>
            );
        }

        getRows(options = {}) {
            const columns = this.getColumns(options);
            const rows = this.props.data
                .slice(0, 20)
                .map((row) =>
                    columns.map((column, i) => (
                        <td
                            key={i}
                            className={css.td}
                            style={{ minWidth: column.width, maxWidth: column.width }}
                            width={column.width}
                        >
                            <div>{htmlRenderer(row, column)}</div>
                        </td>
                    ))
                )
                .map((row, i) => <tr key={i}>{row}</tr>);

            return <tbody key={'body'}>{rows}</tbody>;
        }

        setTableRef = (node) => {
            if (node) this.tableWidth = node.clientWidth;

            this.tableRef.current = node;
            this.forwardedRef && this.forwardedRef(node);
        };

        getScrollbarSize = () => {
            return this.scrollbarWidth;
        };

        onResize = ({ height, width }) => {
            if (width === this.tableWidth) return;
            this.tableWidth = width;
            this.updateColumnWidths();
        };

        render() {
            const { columns } = this.state;
            return (
                <>
                    <div className={css.shadowTableContainer} style={{ marginRight: this.scrollbarWidth + 'px' }}>
                        <div className={css.shadowTable} ref={this.setColumnWidths}>
                            <div className={css.fixedLayout}>
                                <table className={cn(css.table, this.props.className)}>
                                    {this.getHeaders({ fixed: true })}
                                    {this.getRows({ fixed: true })}
                                </table>
                            </div>
                            <div className={css.flexibleLayout}>
                                <table className={cn(css.table, this.props.className)}>
                                    {this.getHeaders({ fixed: false })}
                                    {this.getRows({ fixed: false })}
                                </table>
                            </div>
                        </div>
                    </div>

                    <Table
                        {...this.props}
                        onResize={this.onResize}
                        forwardedRef={this.setTableRef}
                        columns={columns}
                        getScrollbarSize={this.getScrollbarSize}
                    />
                </>
            );
        }
    };
};

const getColumnType = (interval) => {
    const intervals = {
        minute: { component: Counter, props: { interval: 'minute' } },
        second: { component: Counter, props: { interval: 'second' } },
        hour: { component: Counter, props: { interval: 'hour' } },
    };

    return (
        intervals[interval] || {
            component: undefined,
            props: undefined,
        }
    );
};

const counterTextProps = { fontSize: 'inherit' };
const Counter = ({ extraProps, column, rowData }) => {
    const date = get(rowData, `${column.key}.rendered`);
    if (!date) {
        return null;
    }

    return (
        <Cell column={column}>
            <TimeCounter {...extraProps} _text={counterTextProps} dueText={column.timerDueText} date={date} />
        </Cell>
    );
};

export const normalizedTable = (Table) => {
    const NormalizedTable = (props) => {
        const {
            results = [],
            columns = [],
            columnWidths = {},
            multiSelectable = false,
            colorBlend,
            sortOptions,
            searchFilterValue = '',
            canSort = true,
            canResize = true,
            ...rest
        } = props;

        const searchFilterValueRef = React.useRef(searchFilterValue);
        searchFilterValueRef.current = searchFilterValue;

        const className = React.useRef(css.table + new Date().getTime()).current;
        const disableDrag = get(props, disableDrag, true);
        const backgroundColors = colorBlend ? ['coolGray.100', 'dark.100'] : ['light.50', 'dark.50'];
        const backgroundHoverColors = colorBlend ? ['coolGray.200', 'dark.200'] : ['light.200', 'dark.200'];
        const [primary, lightBg, darkBg] = useColor('primary.500', ...backgroundColors);
        const [lightBgHover, darkBgHover] = useColor(...backgroundHoverColors);
        const [selectionBg, selectionBgHover] = useColor('info.100', 'info.50');

        const background = useColorModeValue(lightBg, darkBg);
        const backgroundHover = useColorModeValue(lightBgHover, darkBgHover);
        const containerStyle = React.useMemo(() => ({ background }), background);
        const style = `
            .${className} {
                background: ${background} !important;
                --table-row-bg: ${background};
                --table-selection-bg: ${selectionBg};
            }

            .${className} .${css.tr}[class*="row--hovered"],
            .${className} .${css.tr}.${css.dataRow}.row-background:hover {
                --table-row-bg: ${backgroundHover};
                --table-selection-bg: ${selectionBgHover};
            }

            .${className} .${css.th}:hover .${css.sortIcon},
            .${className} [class*="BaseTable__header-cell--sorting"] .${css.sortIcon} {
                background: ${background};
                color: ${primary};
            }

            .${className} .${css.th} .${css.sortIcon}:hover:before,
            .${className} [class*="BaseTable__header-cell--sorting"] .${css.sortIcon}:before {
                background-color: ${primary};
            }

            .${className} .${css.th}.${css.alignRight}[class*="--sortable"]:hover .${css.thInner},
            .${className} .${css.th}.${css.alignRight}[class*="-cell--sorting"] .${css.thInner}{
                padding-right: 30px;
                transition: .3s padding-right;
            }
        `;

        const extraProps = React.useMemo(
            () => ({
                headerCellProps: ({ columnIndex, column }) => ({
                    className: css.th,
                    tagName: DraggableHeaderCell,
                    index: columnIndex,
                    disableDrag: disableDrag || !!column.frozen,
                }),
                rowCellProps: () => ({ className: css.td }),
                sortBy: {
                    key: get(sortOptions, 'field'),
                    order: get(sortOptions, 'direction'),
                },
            }),
            [disableDrag, sortOptions, disableDrag]
        );

        const hiddenColumns = React.useMemo(() => deriveHiddenColumns(columns, results), [columns, results]);

        const _columns = React.useMemo(
            () =>
                filter(columns, (c) => !hiddenColumns.includes(c.name)).map((column, index) => ({
                    ...column,
                    sortable: canSort && column.sortable,
                    disableDrag,
                    resizable: canResize,
                    width: columnWidths[column.name],
                    key: column.name,
                    cellRenderer: getColumnType(column.interval).component,
                    extraProps: getColumnType(column.interval).props,
                    headerClassName: cn(css.th, {
                        [css.numeric]: column.numeric,
                        [css.alignRight]: column.align === 'right',
                    }),
                    className: cn(css.td, { [css.numeric]: column.numeric }),
                })),
            [columns, columnWidths, hiddenColumns, disableDrag, canResize, canSort]
        );

        const _results = React.useMemo(() => results.map((row) => ({ ...row, _id: row.id.raw })), [results]);

        return (
            <React.Fragment>
                {<style>{style}</style>}
                <Table
                    {...extraProps}
                    {...rest}
                    classPrefix="BaseTable"
                    searchFilterValueRef={searchFilterValueRef}
                    containerStyle={containerStyle}
                    className={cn(className, extraProps.className, rest.className)}
                    rowKey="_id"
                    columnWidths={columnWidths}
                    selectable={multiSelectable}
                    columns={_columns}
                    data={_results}
                    canReorderColumns={disableDrag}
                />
            </React.Fragment>
        );
    };
    return NormalizedTable;
};

const _Table = connect()(tableFactory());
export const Table = normalizedTable(_Table);
export const TableWithAutosizedColumns = normalizedTable(autosizedTableFactory(_Table));
